import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiDatabase, FiTrash2, FiEye, FiRefreshCw, FiDownload, FiPlus, FiExternalLink, FiEdit, FiSave, FiX } from 'react-icons/fi';

const RawData = () => {
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('users');
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState('all');
  const [analyticsSubcategory, setAnalyticsSubcategory] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [newDocumentData, setNewDocumentData] = useState({});
  const [addingDocument, setAddingDocument] = useState(false);

  // Define collection schemas with proper field mappings
  const collectionSchemas = {
    users: {
      display: 'Users',
      fields: ['_id', 'email', 'username', 'firstName', 'lastName', 'isVerified', 'createdAt'],
      searchableFields: ['email', 'username', 'firstName', 'lastName'],
      relations: {
        vendorProfile: 'VendorProfile'
      }
    },
    analytics: {
      display: 'Analytics',
      fields: ['_id', 'event', 'category', 'regionRequest', 'userId', 'sessionId', 'data', 'timestamp'],
      searchableFields: ['event', 'category'],
      subcategories: {
        'region_request': 'Region Requests',
        'user_action': 'User Actions',
        'auth': 'Authentication',
        'vendor_setup': 'Vendor Setup',
        'general': 'General'
      },
      relations: {
        userId: 'User'
      }
    },
    countries: {
      display: 'Countries',
      fields: ['_id', 'name', 'code', 'created_at'],
      searchableFields: ['name', 'code']
    },
    states: {
      display: 'States',
      fields: ['_id', 'name', 'code', 'country_id', 'created_at'],
      searchableFields: ['name', 'code'],
      relations: {
        country_id: 'Country'
      }
    },
    supportedregions: {
      display: 'Supported Regions',
      fields: ['_id', 'country_id', 'state_id', 'isActive', 'createdAt'],
      relations: {
        country_id: 'Country',
        state_id: 'State'
      }
    },
    businesstypes: {
      display: 'Business Types',
      fields: ['_id', 'activity', 'description', 'isActive', 'createdAt'],
      searchableFields: ['activity', 'description']
    },
    categories: {
      display: 'Categories',
      fields: ['_id', 'name', 'description', 'isActive', 'createdAt'],
      searchableFields: ['name', 'description']
    },
    subcategories: {
      display: 'Subcategories',
      fields: ['_id', 'subcategory', 'description', 'business_type_id', 'is_active', 'created_at'],
      searchableFields: ['subcategory', 'description'],
      relations: {
        business_type_id: 'BusinessType'
      }
    },
    posts: {
      display: 'Posts',
      fields: ['_id', 'title', 'content', 'author', 'status', 'views', 'createdAt'],
      searchableFields: ['title', 'content', 'status'],
      relations: {
        author: 'User'
      }
    },
    vendorprofiles: {
      display: 'Vendor Profiles',
      fields: ['_id', 'firstName', 'lastName', 'businessName', 'businessTypeId', 'location', 'userId', 'createdAt'],
      searchableFields: ['firstName', 'lastName', 'businessName', 'location'],
      relations: {
        businessTypeId: 'BusinessType',
        userId: 'User'
      }
    },
    venueprofiles: {
      display: 'Venue Profiles',
      fields: ['_id', 'companyName', 'businessEmail', 'phoneNumber', 'venueType', 'capacity', 'userId', 'isActive', 'createdAt'],
      searchableFields: ['companyName', 'businessEmail', 'venueType'],
      relations: {
        userId: 'User'
      }
    },
    settings: {
      display: 'Settings',
      fields: ['_id', 'key', 'value', 'description', 'isActive', 'updatedAt'],
      searchableFields: ['key', 'description']
    },
    admins: {
      display: 'Administrators',
      fields: ['_id', 'email', 'username', 'firstName', 'lastName', 'role', 'isActive', 'createdAt'],
      searchableFields: ['email', 'username', 'firstName', 'lastName', 'role']
    },
    countryselections: {
      display: 'Country Selections',
      fields: ['_id', 'email', 'country_id', 'state_id', 'userId', 'timestamp'],
      relations: {
        country_id: 'Country',
        state_id: 'State',
        userId: 'User'
      }
    },
    pinverifications: {
      display: 'PIN Verifications',
      fields: ['_id', 'email', 'pin', 'isVerified', 'expiresAt', 'createdAt'],
      searchableFields: ['email']
    },
    unsupportedfields: {
      display: 'Unsupported Fields',
      fields: ['_id', 'fieldName', 'fieldValue', 'context', 'createdAt'],
      searchableFields: ['fieldName', 'fieldValue', 'context']
    },
    venuetypes: {
      display: 'Venue Types',
      fields: ['_id', 'name', 'description', 'category', 'isActive', 'createdAt'],
      searchableFields: ['name', 'description', 'category'],
      addableFields: ['name', 'description'] // User-friendly fields for adding
    },
    venuepermissions: {
      display: 'Venue Permissions',
      fields: ['_id', 'name', 'description', 'category', 'isActive', 'createdAt'],
      searchableFields: ['name', 'description', 'category'],
      addableFields: ['name', 'description'] // User-friendly fields for adding
    },
    venueservices: {
      display: 'Venue Services',
      fields: ['_id', 'name', 'description', 'category', 'isActive', 'createdAt'],
      searchableFields: ['name', 'description', 'category'],
      addableFields: ['name', 'description'] // User-friendly fields for adding
    },
    othervendors: {
      display: 'Other Vendors',
      fields: ['_id', 'userId', 'name', 'email', 'businessType', 'businessName', 'location', 'hasPerk', 'registeredAt'],
      searchableFields: ['name', 'email', 'businessName', 'businessType', 'location'],
      relations: {
        userId: 'User'
      },
      addableFields: ['name', 'email', 'businessType', 'businessName', 'location']
    },
    eventtypes: {
      display: 'Event Types',
      fields: ['_id', 'name', 'description', 'subTypes', 'isActive', 'createdAt', 'updatedAt'],
      searchableFields: ['name', 'description'],
      addableFields: ['name', 'description']
    },
    eventrequests: {
      display: 'Event Requests',
      fields: ['_id', 'eventType', 'eventSubType', 'eventName', 'customerName', 'customerEmail', 'customerPhone', 'eventDate', 'attendeeCount', 'budget', 'status', 'createdAt'],
      searchableFields: ['eventName', 'customerName', 'customerEmail', 'eventType', 'status'],
      relations: {
        approvedBy: 'User'
      },
      addableFields: ['eventType', 'eventSubType', 'eventName', 'customerName', 'customerEmail', 'customerPhone', 'eventDate', 'attendeeCount', 'budget']
    },
    perks: {
      display: 'Perks',
      fields: ['_id', 'title', 'description', 'price', 'businessType', 'duration', 'availability', 'features', 'packageType', 'expiryDate', 'vendorId', 'createdAt'],
      searchableFields: ['title', 'description', 'businessType', 'packageType'],
      relations: {
        vendorId: 'User'
      },
      addableFields: ['title', 'description', 'price', 'businessType', 'duration', 'availability', 'features', 'packageType']
    },
    chats: {
      display: 'Chats',
      fields: ['_id', 'participants', 'lastMessage', 'createdAt', 'updatedAt'],
      searchableFields: ['lastMessage'],
      relations: {
        participants: 'User'
      }
    },
    regionrequests: {
      display: 'Region Requests',
      fields: ['_id', 'country', 'state', 'email', 'reason', 'status', 'createdAt'],
      searchableFields: ['country', 'state', 'email', 'reason', 'status'],
      addableFields: ['country', 'state', 'email', 'reason']
    },
    inclusiveservices: {
      display: 'Inclusive Services',
      fields: ['_id', 'name', 'description', 'category', 'isActive', 'createdAt'],
      searchableFields: ['name', 'description', 'category'],
      addableFields: ['name', 'description', 'category']
    }
  };

  // Fetch available collections from backend
  const fetchCollections = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/management/collections`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        const collectionNames = result.data.map(col => col.name);
        setCollections(collectionNames);
        
        // Set default collection if current selection is not available
        if (!collectionNames.includes(selectedCollection)) {
          setSelectedCollection(collectionNames[0] || 'users');
        }
      } else {
        throw new Error('Failed to fetch collections');
      }
    } catch (err) {
      console.error('Fetch collections error:', err);
      // Fallback to predefined collections
      setCollections(Object.keys(collectionSchemas));
    }
  }, [selectedCollection]);

  // Fetch collection data from backend with proper population
  const fetchCollectionData = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('adminToken');
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50', // Increased limit for better data visibility
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const apiUrl = `${process.env.REACT_APP_API_URL}/admin/management/collection/${selectedCollection}?${params}`;
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        const documents = result.data.documents || [];
        
        console.log(`📊 Fetched ${selectedCollection}:`, {
          total: documents.length,
          sample: documents.length > 0 ? documents[0] : null
        });
        
        setData(documents);
        setPagination(result.data.pagination || { page: 1, pages: 1, total: 0 });
        setLastUpdated(new Date());
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(`API Error: ${response.status} - ${errorData.message || response.statusText}`);
      }
    } catch (err) {
      console.error('💥 Fetch collection error:', err);
      setError(`Failed to load collection data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedCollection, searchTerm]);

  // Filter data based on analytics subcategory
  const applyFilters = useCallback(() => {
    let filtered = [...data];
    
    // Apply analytics subcategory filter
    if (selectedCollection === 'analytics' && analyticsSubcategory !== 'all') {
      filtered = filtered.filter(item => item.category === analyticsSubcategory);
    }
    
    // Apply search filter
    if (searchTerm && searchField !== 'all') {
      const schema = collectionSchemas[selectedCollection];
      if (schema && schema.searchableFields) {
        filtered = filtered.filter(item => {
          if (searchField === 'all') {
            return schema.searchableFields.some(field => 
              item[field]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
            );
          } else {
            return item[searchField]?.toString().toLowerCase().includes(searchTerm.toLowerCase());
          }
        });
      }
    }

    setFilteredData(filtered);
  }, [data, selectedCollection, analyticsSubcategory, searchTerm, searchField]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCollectionData(pagination.page);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchCollectionData, pagination.page]);

  // Initial data load
  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  useEffect(() => {
    fetchCollectionData();
  }, [fetchCollectionData]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Reset filters when switching collections
  useEffect(() => {
    setAnalyticsSubcategory('all');
    setSearchTerm('');
    setSearchField('all');
  }, [selectedCollection]);

    const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/management/collection/${selectedCollection}/${documentId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        setSuccess('Document deleted successfully');
        fetchCollectionData(pagination.page);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete document');
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleAddDocument = async () => {
    setAddingDocument(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/admin/management/collection/${selectedCollection}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(newDocumentData)
        }
      );

      if (response.ok) {
        setSuccess('Document added successfully');
        setShowAddModal(false);
        setNewDocumentData({});
        fetchCollectionData(pagination.page);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add document');
      }
    } catch (err) {
      setError(err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setAddingDocument(false);
    }
  };

  const handleOpenAddModal = () => {
    const schema = collectionSchemas[selectedCollection];
    const initialData = {};
    
    // Use addableFields if available, otherwise use all fields except _id
    const fieldsToUse = schema?.addableFields || schema?.fields?.filter(field => field !== '_id') || [];
    
    // Pre-populate with default values based on collection type
    fieldsToUse.forEach(field => {
      switch (field) {
        case 'createdAt':
        case 'updatedAt':
        case 'timestamp':
        case 'created_at':
          initialData[field] = new Date().toISOString();
          break;
        case 'isActive':
        case 'isVerified':
          initialData[field] = true;
          break;
        default:
          initialData[field] = '';
      }
    });
    
    setNewDocumentData(initialData);
    setShowAddModal(true);
  };

  const handleViewDocument = (doc) => {
    setSelectedDocument(doc);
    setShowViewModal(true);
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Helper function to render field values with proper formatting
  const renderFieldValue = (fieldName, value, document) => {
    if (!value) return <span style={{ color: '#888' }}>-</span>;

    // Handle different field types
    switch (fieldName) {
      case '_id':
        return <code style={{ fontSize: '12px', background: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }}>{value}</code>;
      
      case 'createdAt':
      case 'updatedAt':
      case 'timestamp':
      case 'created_at':
        return new Date(value).toLocaleString();
      
      case 'regionRequest':
        if (typeof value === 'object' && value.country && value.state) {
          // Show enriched country and state names if available
          const countryName = value.countryData?.name || value.country;
          const stateName = value.stateData?.name || value.state;
          return (
            <div>
              <div><strong>Country:</strong> {countryName}</div>
              <div><strong>State:</strong> {stateName}</div>
              {value.isNewRegion && <span style={{ color: '#e74c3c', fontSize: '12px' }}>New Region Request</span>}
            </div>
          );
        }
        return JSON.stringify(value);
      
      case 'data':
        if (typeof value === 'object') {
          return (
            <details style={{ maxWidth: '300px' }}>
              <summary style={{ cursor: 'pointer', color: '#3498db' }}>View Data</summary>
              <pre style={{ fontSize: '11px', margin: '8px 0', overflow: 'auto', maxHeight: '200px' }}>
                {JSON.stringify(value, null, 2)}
              </pre>
            </details>
          );
        }
        return value?.toString() || '-';
      
      default:
        // Handle populated/referenced fields
        if (typeof value === 'object' && value._id) {
          return (
            <div>
              <div><strong>ID:</strong> <code style={{ fontSize: '10px' }}>{value._id}</code></div>
              {value.name && <div><strong>Name:</strong> {value.name}</div>}
              {value.email && <div><strong>Email:</strong> {value.email}</div>}
              {value.activity && <div><strong>Activity:</strong> {value.activity}</div>}
            </div>
          );
        }
        
        if (Array.isArray(value)) {
          return (
            <div>
              {value.map((item, index) => (
                <div key={index} style={{ marginBottom: '4px' }}>
                  {typeof item === 'object' ? JSON.stringify(item) : item}
                </div>
              ))}
            </div>
          );
        }
        
        return value?.toString() || '-';
    }
  };

  const getDisplayTitle = () => {
    const schema = collectionSchemas[selectedCollection];
    if (!schema) return selectedCollection;
    
    if (selectedCollection === 'analytics' && analyticsSubcategory !== 'all') {
      return `${schema.display} - ${schema.subcategories[analyticsSubcategory]}`;
    }
    
    return schema.display;
  };

  // Sort filtered data
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const currentSchema = collectionSchemas[selectedCollection];

  return (
    <Container>
      <Header>
        <div>
          <Title>
            <FiDatabase /> {getDisplayTitle()}
          </Title>
          <Subtitle>
            Live database content - Auto-refreshes every 30 seconds
            {lastUpdated && <span> • Last updated: {lastUpdated.toLocaleTimeString()}</span>}
          </Subtitle>
        </div>
        <HeaderActions>
          <AddButton onClick={handleOpenAddModal}>
            <FiPlus />
            Add New
          </AddButton>
          <RefreshButton onClick={() => fetchCollectionData(pagination.page)} disabled={loading}>
            <FiRefreshCw style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Loading...' : 'Refresh'}
          </RefreshButton>
        </HeaderActions>
      </Header>

      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}

      <Controls>
        <ControlGroup>
          <Label>Collection</Label>
          <Select 
            value={selectedCollection} 
            onChange={(e) => setSelectedCollection(e.target.value)}
          >
            {collections.map(collection => (
              <option key={collection} value={collection}>
                {collectionSchemas[collection]?.display || collection}
              </option>
            ))}
          </Select>
        </ControlGroup>

        {selectedCollection === 'analytics' && currentSchema?.subcategories && (
          <ControlGroup>
            <Label>Category Filter</Label>
            <Select 
              value={analyticsSubcategory} 
              onChange={(e) => setAnalyticsSubcategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {Object.entries(currentSchema.subcategories).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </Select>
          </ControlGroup>
        )}

        <ControlGroup>
          <Label>Search</Label>
          <SearchInput
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </ControlGroup>
      </Controls>

      <StatsBar>
        <Stat>
          <strong>Total:</strong> {pagination.total || 0}
        </Stat>
        <Stat>
          <strong>Filtered:</strong> {filteredData.length}
        </Stat>
        <Stat>
          <strong>Page:</strong> {pagination.page} of {pagination.pages}
        </Stat>
      </StatsBar>

      <TableContainer>
        <Table>
          <thead>
            <tr>
              {currentSchema?.fields.map(field => (
                <Th key={field} onClick={() => handleSort(field)}>
                  {field}
                  {sortConfig.key === field && (
                    <span style={{ marginLeft: '4px' }}>
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </Th>
              )) || <Th>Data</Th>}
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((doc, index) => (
              <tr key={doc._id || index}>
                {currentSchema?.fields.map(field => (
                  <Td key={field}>
                    {renderFieldValue(field, doc[field], doc)}
                  </Td>
                )) || (
                  <Td>
                    <pre style={{ fontSize: '11px', overflow: 'auto', maxHeight: '100px' }}>
                      {JSON.stringify(doc, null, 2)}
                    </pre>
                  </Td>
                )}
                <ActionsTd>
                  <ActionButton onClick={() => handleViewDocument(doc)}>
                    <FiEye />
                  </ActionButton>
                  <ActionButton 
                    onClick={() => handleDeleteDocument(doc._id)}
                    style={{ color: '#e74c3c' }}
                  >
                    <FiTrash2 />
                  </ActionButton>
                </ActionsTd>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>

      {pagination.pages > 1 && (
        <Pagination>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(pageNum => (
            <PageButton
              key={pageNum}
              active={pageNum === pagination.page}
              onClick={() => fetchCollectionData(pageNum)}
            >
              {pageNum}
            </PageButton>
          ))}
        </Pagination>
      )}

      {/* View Document Modal */}
      {showViewModal && selectedDocument && (
        <Modal onClick={() => setShowViewModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Document Details</h3>
              <CloseButton onClick={() => setShowViewModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <pre>{JSON.stringify(selectedDocument, null, 2)}</pre>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      {/* Add Document Modal */}
      {showAddModal && (
        <Modal onClick={() => setShowAddModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Add New Document to {collectionSchemas[selectedCollection]?.display || selectedCollection}</h3>
              <CloseButton onClick={() => setShowAddModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <AddDocumentForm>
                {(() => {
                  const schema = collectionSchemas[selectedCollection];
                  const fieldsToShow = schema?.addableFields || schema?.fields?.filter(field => field !== '_id') || [];
                  
                  return fieldsToShow.map(field => (
                    <FormGroup key={field}>
                      <FormLabel>
                        {field === 'name' ? 'Name' :
                         field === 'description' ? 'Description (Optional)' :
                         field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                      </FormLabel>
                      {field === 'description' ? (
                        <FormTextarea
                          placeholder={`Enter description for this ${selectedCollection.slice(0, -1)}`}
                          value={newDocumentData[field] || ''}
                          onChange={(e) => {
                            setNewDocumentData(prev => ({ ...prev, [field]: e.target.value }));
                          }}
                        />
                      ) : field === 'data' || field === 'regionRequest' ? (
                        <FormTextarea
                          placeholder={`Enter JSON for ${field}`}
                          value={typeof newDocumentData[field] === 'object' 
                            ? JSON.stringify(newDocumentData[field], null, 2) 
                            : newDocumentData[field] || ''}
                          onChange={(e) => {
                            try {
                              const parsed = JSON.parse(e.target.value);
                              setNewDocumentData(prev => ({ ...prev, [field]: parsed }));
                            } catch {
                              setNewDocumentData(prev => ({ ...prev, [field]: e.target.value }));
                            }
                          }}
                        />
                      ) : (
                        <FormInput
                          type={
                            field.includes('email') || field.includes('Email') ? 'email' :
                            field.includes('phone') || field.includes('Phone') ? 'tel' :
                            field.includes('password') || field.includes('Password') ? 'password' :
                            field.includes('Date') || field.includes('At') || field === 'timestamp' ? 'datetime-local' :
                            field.includes('isActive') || field.includes('isVerified') || field.includes('is') ? 'checkbox' :
                            'text'
                          }
                          placeholder={
                            field === 'name' ? `Enter ${selectedCollection.slice(0, -1)} name` :
                            `Enter ${field}`
                          }
                          checked={field.includes('is') ? newDocumentData[field] : undefined}
                          value={field.includes('is') ? undefined : newDocumentData[field] || ''}
                          onChange={(e) => {
                            const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
                            setNewDocumentData(prev => ({ ...prev, [field]: value }));
                          }}
                          required={field === 'name'}
                        />
                      )}
                      <FormHint>
                        {field === 'name' && `The name of this ${selectedCollection.slice(0, -1)} (required)`}
                        {field === 'description' && 'Optional description to provide more details'}
                        {field === 'email' && 'Enter a valid email address'}
                        {field === 'data' && 'Enter valid JSON object'}
                        {field === 'regionRequest' && 'Enter JSON with country, state, countryId, stateId'}
                        {field.includes('Date') && 'Select date and time'}
                      </FormHint>
                    </FormGroup>
                  ));
                })()}
                
                <FormActions>
                  <CancelButton onClick={() => setShowAddModal(false)}>
                    <FiX /> Cancel
                  </CancelButton>
                  <SaveButton 
                    onClick={handleAddDocument}
                    disabled={addingDocument}
                  >
                    <FiSave />
                    {addingDocument ? 'Adding...' : 'Add Document'}
                  </SaveButton>
                </FormActions>
              </AddDocumentForm>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 100%;
  overflow-x: auto;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Title = styled.h1`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #2c3e50;
  margin: 0;
  font-size: 24px;
  font-weight: 600;

  svg {
    color: #3498db;
  }
`;

const Subtitle = styled.p`
  color: #7f8c8d;
  margin: 8px 0 0 0;
  font-size: 14px;
  font-weight: 400;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 10px;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(46, 204, 113, 0.3);

  &:hover {
    background: linear-gradient(135deg, #229954, #27ae60);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(46, 204, 113, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  background: linear-gradient(135deg, #3498db, #5dade2);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);

  &:hover {
    background: linear-gradient(135deg, #2980b9, #3498db);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(52, 152, 219, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  &:active {
    transform: translateY(0);
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Controls = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #2c3e50;
  font-size: 14px;
`;

const Select = styled.select`
  padding: 10px 14px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  background: white;
  min-width: 160px;
  font-size: 14px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  }
`;

const SearchInput = styled.input`
  padding: 10px 14px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  min-width: 240px;
  font-size: 14px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  }

  &::placeholder {
    color: #95a5a6;
  }
`;

const StatsBar = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  background: white;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Stat = styled.div`
  font-size: 14px;
  color: #2c3e50;

  strong {
    color: #3498db;
    font-weight: 600;
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 15px;
  border-left: 4px solid #e74c3c;
`;

const SuccessMessage = styled.div`
  background: #efe;
  color: #393;
  padding: 10px;
  border-radius: 6px;
  margin-bottom: 15px;
  border-left: 4px solid #27ae60;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border: 1px solid #e1e5e9;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
`;

const Th = styled.th`
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  padding: 16px 12px;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #2c3e50;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  transition: background 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #e9ecef, #dee2e6);
  }

  &:first-child {
    border-top-left-radius: 12px;
  }

  &:last-child {
    border-top-right-radius: 12px;
  }
`;

const Td = styled.td`
  padding: 14px 12px;
  border-bottom: 1px solid #f1f3f4;
  vertical-align: top;
  max-width: 300px;
  word-wrap: break-word;
  font-size: 13px;
  transition: background 0.2s ease;

  tr:hover & {
    background: rgba(52, 152, 219, 0.02);
  }

  tr:last-child & {
    border-bottom: none;
  }
`;

const ActionsTd = styled.td`
  padding: 14px 12px;
  border-bottom: 1px solid #f1f3f4;
  white-space: nowrap;
  
  tr:hover & {
    background: rgba(52, 152, 219, 0.02);
  }

  tr:last-child & {
    border-bottom: none;
  }
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  margin: 0 2px;
  color: #3498db;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(52, 152, 219, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  gap: 5px;
  margin-top: 20px;
`;

const PageButton = styled.button`
  padding: 8px 12px;
  border: 1px solid #ddd;
  background: ${props => props.active ? '#3498db' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${props => props.active ? '#2980b9' : '#f8f9fa'};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ddd;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;

  &:hover {
    color: #333;
  }
`;

const ModalBody = styled.div`
  padding: 20px;
  overflow: auto;
  flex: 1;

  pre {
    font-size: 12px;
    line-height: 1.4;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
`;

const AddDocumentForm = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #2c3e50;
  font-size: 14px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  }

  &[type="checkbox"] {
    width: auto;
    margin-right: 8px;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 6px;
  font-size: 14px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  min-height: 120px;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  }
`;

const FormHint = styled.small`
  display: block;
  margin-top: 4px;
  color: #7f8c8d;
  font-size: 12px;
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #ecf0f1;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #229954, #27ae60);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CancelButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: transparent;
  color: #7f8c8d;
  border: 2px solid #bdc3c7;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    color: #2c3e50;
    border-color: #95a5a6;
    background: #f8f9fa;
  }
`;

export default RawData;
